import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimerIcon from '@mui/icons-material/Timer';
import {
  emailDeliveryReportService,
  eventService,
  paymentThroughputReportService,
  type EmailDeliveryMissingItem,
  type EmailDeliveryPerEvent,
} from '../services';
import { formatNumber } from '../../../utils';

interface KpiCardProps {
  title: string;
  value: number;
  total: number;
  color: string;
  icon: React.ReactNode;
  hint?: string;
  valueLabel?: string;
  footer?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  total,
  color,
  icon,
  hint,
  valueLabel,
  footer,
}) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${color}1a`,
              color,
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="h4" fontWeight={700} sx={{ color }}>
          {valueLabel ?? formatNumber(value)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {footer ??
            (total > 0
              ? `${pct.toFixed(1)}% de ${formatNumber(total)}`
              : 'sem dados no período')}
        </Typography>
        {hint && (
          <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const formatDateBR = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
};

const formatMs = (ms: number) => {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
};

const providerLabel = (p: string) => {
  if (p === 'mercadopago') return 'Mercado Pago';
  if (p === 'pagarme') return 'Pagar.me';
  return p;
};

type EventOption = { _id: string; title: string };

const EventFilterSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: EventOption[];
}> = ({ value, onChange, options }) => (
  <FormControl size="small" sx={{ minWidth: 240 }}>
    <InputLabel id="event-filter-label">Filtrar por evento</InputLabel>
    <Select
      labelId="event-filter-label"
      label="Filtrar por evento"
      value={value}
      onChange={(e) => onChange(String(e.target.value))}
    >
      <MenuItem value="">Todos os eventos</MenuItem>
      {options.map((ev) => (
        <MenuItem key={ev._id} value={ev._id}>
          {ev.title}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const EmailDeliveryReportPanel: React.FC<{
  eventOptions: EventOption[];
}> = ({ eventOptions }) => {
  const queryClient = useQueryClient();
  const [eventFilter, setEventFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-reports', 'email-delivery', eventFilter, page, rowsPerPage],
    queryFn: () =>
      emailDeliveryReportService.get({
        eventId: eventFilter || undefined,
        page: page + 1,
        limit: rowsPerPage,
      }),
    placeholderData: (prev) => prev,
  });

  const report = data?.data;

  const resendMutation = useMutation({
    mutationFn: (params: { email: string; eventSlug: string }) =>
      emailDeliveryReportService.resendByEmail(params),
    onSuccess: (result: { success: boolean; message?: string }) => {
      setSnackbar({
        open: true,
        message: result?.message || 'Reenvio enfileirado com sucesso.',
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-reports', 'email-delivery'] });
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Não foi possível reenviar o email agora. Tente de novo em alguns minutos.',
        severity: 'error',
      });
    },
  });

  const handleResend = (item: EmailDeliveryMissingItem) => {
    if (!item.participantEmail || !item.eventSlug) {
      setSnackbar({
        open: true,
        message: 'Inscrição sem email ou evento válido — não foi possível reenviar.',
        severity: 'error',
      });
      return;
    }
    resendMutation.mutate({ email: item.participantEmail, eventSlug: item.eventSlug });
  };

  const kpis = report?.kpis;
  const perEvent: EmailDeliveryPerEvent[] = report?.perEvent ?? [];
  const missingItems: EmailDeliveryMissingItem[] = report?.missing?.items ?? [];
  const missingTotal = report?.missing?.pagination?.total ?? 0;

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end" mb={2}>
        <EventFilterSelect
          value={eventFilter}
          onChange={(v) => {
            setEventFilter(v);
            setPage(0);
          }}
          options={eventOptions}
        />
        <Tooltip title="Atualizar">
          <IconButton onClick={() => refetch()} disabled={isFetching}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {report?.trackingStartAt && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Os números abaixo consideram apenas inscrições pagas criadas a partir de{' '}
          <strong>{formatDateBR(report.trackingStartAt)}</strong>, quando o rastreamento de
          envios começou a ser registrado.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">
          Não foi possível carregar o relatório agora. Tente novamente em instantes.
        </Alert>
      ) : (
        <>
          {isFetching && <LinearProgress sx={{ mb: 2 }} />}

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Inscrições pagas"
                value={kpis?.totalPaid ?? 0}
                total={kpis?.totalPaid ?? 0}
                color="#1976d2"
                icon={<MailOutlineIcon fontSize="small" />}
                hint="Base do relatório no período"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Email enviado"
                value={kpis?.withEmailSuccess ?? 0}
                total={kpis?.totalPaid ?? 0}
                color="#2e7d32"
                icon={<CheckCircleIcon fontSize="small" />}
                hint="Ao menos uma tentativa bem-sucedida"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Falhou em todas as tentativas"
                value={kpis?.withEmailFailureOnly ?? 0}
                total={kpis?.totalPaid ?? 0}
                color="#ed6c02"
                icon={<ErrorOutlineIcon fontSize="small" />}
                hint="Logs existem mas nenhum com sucesso"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Sem nenhum envio"
                value={kpis?.withoutEmail ?? 0}
                total={kpis?.totalPaid ?? 0}
                color="#d32f2f"
                icon={<ReportProblemIcon fontSize="small" />}
                hint="Nada foi registrado — exige reenvio"
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box px={2} py={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Cobertura por evento
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Evento</TableCell>
                  <TableCell align="right">Pagas</TableCell>
                  <TableCell align="right">Enviadas</TableCell>
                  <TableCell align="right">Falhou</TableCell>
                  <TableCell align="right">Sem envio</TableCell>
                  <TableCell align="right">Cobertura</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perEvent.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      Nenhuma inscrição paga no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  perEvent.map((row) => {
                    const coverage =
                      row.totalPaid > 0 ? (row.withEmailSuccess / row.totalPaid) * 100 : 0;
                    const coverageColor =
                      coverage >= 95 ? '#2e7d32' : coverage >= 70 ? '#ed6c02' : '#d32f2f';
                    return (
                      <TableRow key={row.eventId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.eventTitle || row.eventId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateBR(row.eventStartDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.totalPaid)}</TableCell>
                        <TableCell align="right">{formatNumber(row.withEmailSuccess)}</TableCell>
                        <TableCell align="right">
                          {row.withEmailFailureOnly > 0
                            ? formatNumber(row.withEmailFailureOnly)
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.withoutEmail > 0 ? formatNumber(row.withoutEmail) : '—'}
                        </TableCell>
                        <TableCell align="right">{coverage.toFixed(0)}%</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined">
            <Box px={2} py={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Inscrições pagas sem registro de email
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Inscrição</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Criada em</TableCell>
                  <TableCell align="right">Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missingItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      Nada por aqui.
                    </TableCell>
                  </TableRow>
                ) : (
                  missingItems.map((item) => (
                    <TableRow key={item.registrationId} hover>
                      <TableCell>{item.participantName || '—'}</TableCell>
                      <TableCell>{item.eventTitle || '—'}</TableCell>
                      <TableCell>{item.participantEmail || '—'}</TableCell>
                      <TableCell>{formatDateBR(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SendIcon fontSize="small" />}
                          disabled={
                            resendMutation.isPending ||
                            !item.participantEmail ||
                            !item.eventSlug
                          }
                          onClick={() => handleResend(item)}
                        >
                          Reenviar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              count={missingTotal}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const PaymentThroughputReportPanel: React.FC<{
  eventOptions: EventOption[];
}> = ({ eventOptions }) => {
  const [hours, setHours] = useState(24);
  const [eventFilter, setEventFilter] = useState('');

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin-reports', 'payment-throughput', hours, eventFilter],
    queryFn: () =>
      paymentThroughputReportService.get({
        hours,
        eventId: eventFilter || undefined,
      }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const report = data?.data;
  const kpis = report?.kpis;
  const perMinute = useMemo(() => {
    const rows = report?.perMinute ?? [];
    return rows.slice(-60).reverse();
  }, [report?.perMinute]);

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ButtonGroup size="small" variant="outlined">
            {[12, 24, 48].map((h) => (
              <Button
                key={h}
                variant={hours === h ? 'contained' : 'outlined'}
                onClick={() => setHours(h)}
              >
                {h}h
              </Button>
            ))}
          </ButtonGroup>
          <EventFilterSelect value={eventFilter} onChange={setEventFilter} options={eventOptions} />
          <Tooltip title="Atualizar">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Link component={RouterLink} to="/payment-queues" variant="body2">
          Ver filas em tempo real
        </Link>
      </Stack>

      {report?.trackingStartAt && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Métricas de webhook a partir de{' '}
          <strong>{formatDateBR(report.trackingStartAt)}</strong>. Janela: últimas{' '}
          <strong>{report.window?.hours ?? hours}h</strong> (desde{' '}
          {formatDateBR(report.window?.from)}). Estimativa observada na VPS atual — não é SLA.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Não foi possível carregar o relatório de pagamentos.</Alert>
      ) : (
        <>
          {isFetching && <LinearProgress sx={{ mb: 2 }} />}

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Confirmados (novos)"
                value={kpis?.confirmed ?? 0}
                total={kpis?.totalAttempts ?? 0}
                color="#2e7d32"
                icon={<CheckCircleIcon fontSize="small" />}
                hint="Primeira confirmação no webhook"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Pico / minuto"
                value={kpis?.peakConfirmedPerMinute ?? 0}
                total={0}
                color="#1976d2"
                icon={<TrendingUpIcon fontSize="small" />}
                footer="Máximo de confirmações em 1 minuto"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Média / hora"
                value={kpis?.avgPerHour ?? 0}
                total={0}
                color="#1976d2"
                icon={<SpeedIcon fontSize="small" />}
                footer="Confirmados ÷ horas da janela"
                valueLabel={String(kpis?.avgPerHour ?? 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="p95 processamento"
                value={kpis?.p95DurationMs ?? 0}
                total={0}
                color="#ed6c02"
                icon={<TimerIcon fontSize="small" />}
                footer="Tempo total do webhook até resposta"
                valueLabel={formatMs(kpis?.p95DurationMs ?? 0)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Taxa de erro"
                value={kpis?.errors ?? 0}
                total={kpis?.totalAttempts ?? 0}
                color="#d32f2f"
                icon={<ErrorOutlineIcon fontSize="small" />}
                footer={`${kpis?.errorRatePct ?? 0}% das tentativas`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiCard
                title="Tentativas (webhooks)"
                value={kpis?.totalAttempts ?? 0}
                total={kpis?.totalAttempts ?? 0}
                color="#5c6bc0"
                icon={<ReportProblemIcon fontSize="small" />}
                hint={`Idempotentes: ${kpis?.alreadyConfirmed ?? 0}`}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box px={2} py={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Por provedor
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provedor</TableCell>
                  <TableCell align="right">Tentativas</TableCell>
                  <TableCell align="right">Confirmados</TableCell>
                  <TableCell align="right">p50</TableCell>
                  <TableCell align="right">p95</TableCell>
                  <TableCell align="right">Erros %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report?.perProvider ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      Nenhum webhook registrado na janela.
                    </TableCell>
                  </TableRow>
                ) : (
                  (report?.perProvider ?? []).map((row) => (
                    <TableRow key={row.provider} hover>
                      <TableCell>{providerLabel(row.provider)}</TableCell>
                      <TableCell align="right">{formatNumber(row.attempts)}</TableCell>
                      <TableCell align="right">{formatNumber(row.confirmed)}</TableCell>
                      <TableCell align="right">{formatMs(row.p50DurationMs)}</TableCell>
                      <TableCell align="right">{formatMs(row.p95DurationMs)}</TableCell>
                      <TableCell align="right">{row.errorRatePct}%</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box px={2} py={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Por minuto (últimos 60 buckets)
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Minuto</TableCell>
                  <TableCell align="right">Tentativas</TableCell>
                  <TableCell align="right">Confirmados</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {perMinute.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      Sem dados por minuto.
                    </TableCell>
                  </TableRow>
                ) : (
                  perMinute.map((row) => (
                    <TableRow key={row.minute} hover>
                      <TableCell>{formatDateBR(row.minute)}</TableCell>
                      <TableCell align="right">{formatNumber(row.attempts)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={formatNumber(row.confirmed)}
                          color={row.confirmed > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined">
            <Box px={2} py={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Processamentos mais lentos
              </Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quando</TableCell>
                  <TableCell>Provedor</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">PSP</TableCell>
                  <TableCell align="right">Confirmação</TableCell>
                  <TableCell>Resultado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report?.slowest ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      Nenhum registro lento na janela.
                    </TableCell>
                  </TableRow>
                ) : (
                  (report?.slowest ?? []).map((row, i) => (
                    <TableRow key={`${row.finishedAt}-${i}`} hover>
                      <TableCell>{formatDateBR(row.finishedAt)}</TableCell>
                      <TableCell>{providerLabel(row.provider)}</TableCell>
                      <TableCell>{row.eventTitle || row.registrationId || '—'}</TableCell>
                      <TableCell align="right">{formatMs(row.durationTotalMs)}</TableCell>
                      <TableCell align="right">
                        {row.durationPspMs != null ? formatMs(row.durationPspMs) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {row.durationConfirmMs != null ? formatMs(row.durationConfirmMs) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.outcome} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </>
  );
};

const Reports: React.FC = () => {
  const [tab, setTab] = useState(0);

  const { data: eventsData } = useQuery({
    queryKey: ['admin-reports', 'events-options'],
    queryFn: () => eventService.getAll({ page: 1, limit: 500 }),
  });
  const eventOptions = useMemo(
    () => (eventsData?.data?.events ?? []) as EventOption[],
    [eventsData]
  );

  const subtitle =
    tab === 0
      ? 'Acompanhamento de entrega dos emails de confirmação para inscrições pagas.'
      : 'Throughput e latência dos webhooks de confirmação de inscrição (Mercado Pago e Pagar.me).';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box mb={2}>
        <Typography variant="h4" fontWeight={700}>
          Relatórios
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="E-mail" />
        <Tab label="Pagamentos" />
      </Tabs>

      {tab === 0 && <EmailDeliveryReportPanel eventOptions={eventOptions} />}
      {tab === 1 && <PaymentThroughputReportPanel eventOptions={eventOptions} />}
    </Container>
  );
};

export default Reports;
